/**
 * Classic typing-tutor style report card helpers + printable PDF (browser print).
 */

import type { FollowAlongStats } from "../practice/FollowAlongEngine.js";
import {
  computeReportMetrics,
  letterGradeFromAccuracy,
} from "../practice/FollowAlongEngine.js";

export interface ReportCardModel {
  title: string;
  subtitle: string;
  accuracyPct: number;
  letter: string;
  npm: number;
  hits: number;
  wrongs: number;
  perfect: number;
  jitterMs: number;
  meanDeltaMs: number;
  timingNote: string;
  dynamicsNote: string;
  pedalNote: string;
  elapsedSec: number;
  errorByMidi: Record<string, number>;
  passLine?: string;
}

export function buildReportFromStats(
  title: string,
  subtitle: string,
  stats: FollowAlongStats,
  passLine?: string,
): ReportCardModel {
  const m = computeReportMetrics(stats);
  return {
    title,
    subtitle,
    accuracyPct: stats.accuracyPct,
    letter: m.letter,
    npm: m.npm,
    hits: stats.hits,
    wrongs: stats.wrongs,
    perfect: stats.perfect,
    jitterMs: stats.jitterMs,
    meanDeltaMs: stats.meanDeltaMs,
    timingNote: m.timingNote,
    dynamicsNote: m.dynamicsNote,
    pedalNote: m.pedalNote,
    elapsedSec: stats.elapsedSec,
    errorByMidi: stats.errorByMidi,
    passLine,
  };
}

/**
 * Session score shown on the report card.
 * Formula (max theoretical ~10000 for a strong long class):
 *   accuracyPct × 50          — pitch correctness (0–5000)
 *   + perfect × 8             — on-time perfect hits
 *   + hits × 2                — volume of clean practice
 *   − wrongs × 6              — mistakes cost points
 *   + timingSteadyBonus       — up to 400 when jitter is low
 */
export function computeClassSessionScore(stats: FollowAlongStats): number {
  const accuracyPart = Math.round(stats.accuracyPct * 50);
  const perfectPart = Math.round((stats.perfect ?? 0) * 8);
  const hitPart = Math.round(stats.hits * 2);
  const missPenalty = Math.round(stats.wrongs * 6);
  const jitter = stats.jitterMs ?? 0;
  const timingSteadyBonus =
    jitter <= 0 ? 0 : Math.round(Math.max(0, 400 - Math.min(400, jitter * 2)));
  return Math.max(
    0,
    accuracyPart + perfectPart + hitPart - missPenalty + timingSteadyBonus,
  );
}

export function emptyFollowStats(): FollowAlongStats {
  return {
    expectedCount: 0,
    hits: 0,
    wrongs: 0,
    remaining: 0,
    accuracyPct: 100,
    completed: false,
    perfect: 0,
    early: 0,
    late: 0,
    meanDeltaMs: 0,
    jitterMs: 0,
    velocityHits: 0,
    velocityMisses: 0,
    notesPerMinute: 0,
    elapsedSec: 0,
    errorByMidi: {},
  };
}

export { letterGradeFromAccuracy, computeReportMetrics };
